import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "react-query";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider as MuiDivider,
  InputAdornment,
  Paper,
  Popover,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as GetAppIcon,
} from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import Link from "@material-ui/core/Link";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import useFetchData from "../../../../hooks/useFetchData";
import Loader from "../../../../components/Loader";
import { formatter } from "../../../../utils";
import { generateCSV } from "../../../dashboards/Default/utils";
import {
  COLUMNS,
  COMPACT_HIDDEN_COLUMNS,
} from "./meterReadingsHistoryConstants";
import { useColumnFilters } from "./useColumnFilters";
import { usePagination } from "./usePagination";
import MeterReadingsHistoryHeader from "./MeterReadingsHistoryHeader";

import { createGlobalStyle } from "styled-components/macro";

const SpinKeyframes = createGlobalStyle`
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const ToggleButton = styled(Button)`
  font-size: 0.7rem;
  padding: 2px 10px;

  &.active {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: ${(props) => props.theme.palette.primary.contrastText};
    &:hover {
      background-color: ${(props) => props.theme.palette.primary.dark};
    }
  }
`;

const StyledTableContainer = styled(TableContainer)`
  max-height: calc(100vh - 460px);
  overflow: auto;
`;

const Cell = styled(TableCell)`
  padding: ${(props) => props.theme.spacing(1)}px;
  font-size: 0.75rem;
  white-space: nowrap;
  border: 1px solid #eee;
`;

const PlainRow = styled(TableRow)``;

const DataRow = styled(TableRow)`
  cursor: pointer;
  &:hover td {
    background-color: ${(props) => props.theme.palette.grey[100]};
  }
`;

const SelectedDataRow = styled(TableRow)`
  cursor: pointer;
  & td {
    background-color: #fffde7;
  }
  &:hover td {
    background-color: #fff9c4;
  }
`;

const StyledLink = styled.a`
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    color: #1565c0;
  }
`;

const stopProp = (e) => e.stopPropagation();

const MoreButton = styled.button`
  display: block;
  color: ${(props) => props.theme.palette.secondary.main};
  background: none;
  border: 1px solid ${(props) => props.theme.palette.secondary.main};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.65rem;
  font-weight: 600;
  padding: 1px 6px;
  margin-top: 3px;
  &:hover {
    background-color: ${(props) => props.theme.palette.secondary.main};
    color: #fff;
  }
`;

const SummaryHeaderCell = styled(TableCell)`
  font-weight: 600;
  font-size: 0.75rem;
  white-space: nowrap;
  background-color: ${(props) => props.theme.palette.background.default};
  padding: ${(props) => props.theme.spacing(1)}px;
`;

const SummaryCell = styled(TableCell)`
  font-size: 0.75rem;
  white-space: nowrap;
  padding: ${(props) => props.theme.spacing(1)}px;
  border: 1px solid #eee;
`;

const ARRAY_COL_INLINE_LIMIT = 2;

const MONTH_NAMES = {
  1: "January",
  2: "February",
  3: "March",
  4: "April",
  5: "May",
  6: "June",
  7: "July",
  8: "August",
  9: "September",
  10: "October",
  11: "November",
  12: "December",
};

const SUMMARY_COLUMNS = [
  { id: "fiscal_year", label: "Fiscal Year (Sep–Aug)", align: "left" },
  { id: "grouping_name", label: "Permit Owner/Group", align: "left" },
  {
    id: "permitted_gallons",
    label: "Annual Allocation",
    align: "right",
  },
  { id: "metered_gallons", label: "YTD Metered", align: "right" },
  { id: "remaining_gallons", label: "Remaining", align: "right" },
  { id: "pct_pumped", label: "% Pumped", align: "right" },
  { id: "is_overpumped", label: "Over Annual", align: "center" },
  { id: "is_over80pct", label: "Over 80%", align: "center" },
  { id: "last_report", label: "Last Report", align: "left" },
];

const MeterReadingsHistory = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // ── Data: meter readings (full POR) ──
  const { data, isLoading, error } = useQuery(
    ["ui-meter-readings-historical"],
    async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/ui-meter-readings-historical`,
          { headers }
        );
        return data;
      } catch (err) {
        console.error(err);
      }
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  // ── Data: pumping summary (full POR) ──
  const { data: pumpingSummary } = useQuery(
    ["ui-pumping-summary-historical"],
    async () => {
      try {
        const token = await getAccessTokenSilently();
        const headers = { Authorization: `Bearer ${token}` };
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/ui-pumping-summary-historical`,
          { headers }
        );
        return data;
      } catch (err) {
        console.error(err);
      }
    },
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const handleRefresh = useCallback(async () => {
    setConfirmOpen(false);
    try {
      setRefreshing(true);
      const token = await getAccessTokenSilently();
      await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/api/ui-meter-readings-historical/refresh`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await queryClient.invalidateQueries(["ui-meter-readings-historical"]);
      await queryClient.invalidateQueries(["ui-pumping-summary-historical"]);
      setSnackbar({
        open: true,
        severity: "success",
        message: "Data refreshed successfully.",
      });
    } catch (err) {
      console.error("Refresh failed:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Refresh failed. Please try again or contact support.",
      });
    } finally {
      setRefreshing(false);
    }
  }, [getAccessTokenSilently, queryClient]);

  // ── Lookups ──
  const [adjustmentReasonLookup] = useFetchData(
    "ui-list-meter-reading-adjustment-reason",
    [],
    true
  );
  const adjustmentReasonMap = useMemo(() => {
    const m = {};
    if (adjustmentReasonLookup?.length > 0) {
      adjustmentReasonLookup.forEach((item) => {
        m[item.mr_adjustment_reason_ndx] = item.mr_adjustment_reason_desc;
      });
    }
    return m;
  }, [adjustmentReasonLookup]);

  const [reportStatusLookup] = useFetchData(
    "ui-list-meter-reading-report-status",
    [],
    true
  );
  const reportStatusMap = useMemo(() => {
    const m = {};
    if (reportStatusLookup?.length > 0) {
      reportStatusLookup.forEach((item) => {
        m[item.mr_report_status_ndx] = item.mr_report_status_desc;
      });
    }
    return m;
  }, [reportStatusLookup]);

  const [wellsLookup] = useFetchData("ui-list-meter-well-records", [], true);
  const wellsMap = useMemo(() => {
    const m = {};
    if (wellsLookup?.length > 0) {
      wellsLookup.forEach((item) => {
        m[item.well_ndx] = item.district_well_id || item.well_name;
      });
    }
    return m;
  }, [wellsLookup]);

  const [permitsLookup] = useFetchData("ui-list-permits", [], true);
  const permitsMap = useMemo(() => {
    const m = {};
    if (permitsLookup?.length > 0) {
      permitsLookup.forEach((item) => {
        m[item.permit_ndx] = item.permit_number_only;
      });
    }
    return m;
  }, [permitsLookup]);

  const [permitGroupsLookup] = useFetchData(
    "ui-lookup-mr-filters-permitgroupings",
    [],
    true
  );
  const permitGroupsMap = useMemo(() => {
    const m = {};
    if (permitGroupsLookup?.length > 0) {
      permitGroupsLookup.forEach((item) => {
        m[item.grouping_ndx] = item.grouping_name;
      });
    }
    return m;
  }, [permitGroupsLookup]);

  // Stable lookups object for the hook
  const lookups = useMemo(
    () => ({
      months: MONTH_NAMES,
      reportStatuses: reportStatusMap,
      permitGroups: permitGroupsMap,
      wells: wellsMap,
      permits: permitsMap,
      adjustmentReasons: adjustmentReasonMap,
    }),
    [
      reportStatusMap,
      permitGroupsMap,
      wellsMap,
      permitsMap,
      adjustmentReasonMap,
    ]
  );

  // ── Column filtering / sorting (operates on full data) ──
  const {
    columnFilters,
    filterOptions,
    orderBy,
    order,
    searchText,
    setSearchText,
    handleFilterChange,
    handleSort,
    filteredAndSortedRows,
  } = useColumnFilters(data, lookups);

  // ── Compact / Full view toggle ──
  const [compactMode, setCompactMode] = useState(false);

  const visibleColumns = useMemo(
    () =>
      compactMode
        ? COLUMNS.filter((col) => !COMPACT_HIDDEN_COLUMNS.has(col.id))
        : COLUMNS,
    [compactMode]
  );

  // ── Row selection for summary ──
  const [selectedRowNdx, setSelectedRowNdx] = useState(null);

  const handleRowClick = (row) => {
    setSelectedRowNdx((prev) =>
      prev === row.meter_reading_ndx ? null : row.meter_reading_ndx
    );
  };

  const selectedRow = useMemo(() => {
    if (selectedRowNdx == null) return null;
    return filteredAndSortedRows.find(
      (r) => r.meter_reading_ndx === selectedRowNdx
    );
  }, [selectedRowNdx, filteredAndSortedRows]);

  // ── Derive summary rows from selected row's permit groups (all fiscal years) ──
  const selectedSummaryRows = useMemo(() => {
    if (!selectedRow || !pumpingSummary?.length) return [];
    const groupNdxs = new Set(selectedRow.assoc_permit_groups || []);
    if (groupNdxs.size === 0) return [];

    return pumpingSummary
      .filter((s) => groupNdxs.has(s.grouping_ndx))
      .sort((a, b) => {
        const nameCmp = (a.grouping_name || "").localeCompare(
          b.grouping_name || ""
        );
        if (nameCmp !== 0) return nameCmp;
        return b.fiscal_year - a.fiscal_year;
      });
  }, [selectedRow, pumpingSummary]);

  // ── Pagination ──
  const {
    page,
    perPage,
    handleChangePage,
    handleChangePerPage,
    paginatedItems: paginatedRows,
  } = usePagination(filteredAndSortedRows, 60);

  // ── CSV export ──
  const handleExportCSV = useCallback(() => {
    if (!filteredAndSortedRows?.length) return;
    const headers = [
      "Cal Year",
      "Month",
      "Meter ID",
      "Permit Owner/Group",
      "Wells",
      "Permits",
      "Last Rdg",
      "Reading",
      "Adj",
      "Adj Reason",
      "Status",
      "Calc Gallons",
      "Notes",
    ];
    const dataRows = filteredAndSortedRows.map((row) => [
      row.c_year,
      MONTH_NAMES[row.c_month] || row.c_month,
      row.meter_id,
      (row.assoc_permit_groups || [])
        .map((ndx) => lookups.permitGroups[ndx])
        .filter(Boolean)
        .join("; "),
      (row.assoc_wells || [])
        .map((ndx) => lookups.wells[ndx])
        .filter(Boolean)
        .join("; "),
      (row.assoc_permits || [])
        .map((ndx) => lookups.permits[ndx])
        .filter(Boolean)
        .join("; "),
      row.prev_meter_reading,
      row.meter_reading,
      row.adjustment,
      lookups.adjustmentReasons[row.mr_adjustment_reason_ndx] || "",
      lookups.reportStatuses[row.mr_report_status_ndx] || "",
      row.calculated_gallons,
      row.notes || "",
    ]);
    generateCSV(headers, dataRows, "historical-meter-readings.csv");
  }, [filteredAndSortedRows, lookups]);

  // ── Popover state for truncated array cells ──
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverLinks, setPopoverLinks] = useState([]);

  const openPopover = (event, links) => {
    event.stopPropagation();
    setPopoverAnchor(event.currentTarget);
    setPopoverLinks(links);
  };

  const closePopover = () => {
    setPopoverAnchor(null);
    setPopoverLinks([]);
  };

  // ── Render helpers ──
  const buildLink = (ndx, label, path) => (
    <StyledLink
      key={ndx}
      href={path}
      target="_blank"
      rel="noopener noreferrer"
      onClick={stopProp}
    >
      {label}
    </StyledLink>
  );

  const renderTruncatedLinks = (links) => {
    if (!links || links.length === 0) return null;
    const inline = links.slice(0, ARRAY_COL_INLINE_LIMIT);
    const remaining = links.length - ARRAY_COL_INLINE_LIMIT;
    return (
      <>
        {inline.map((link, i) => (
          <span key={link.key}>
            {link}
            {i < inline.length - 1 ? "; " : ""}
          </span>
        ))}
        {remaining > 0 && (
          <MoreButton onClick={(e) => openPopover(e, links)}>
            +{remaining} more
          </MoreButton>
        )}
      </>
    );
  };

  const renderLinkedArray = (arr, lookupMap, pathFn) => {
    if (!arr || !arr.length) return null;
    const unique = [...new Set(arr.filter((ndx) => lookupMap[ndx]))];
    const links = unique.map((ndx) =>
      buildLink(ndx, lookupMap[ndx], pathFn(ndx))
    );
    return renderTruncatedLinks(links);
  };

  const renderPermitGroups = (arr) => {
    if (!arr || !arr.length) return null;
    const unique = [...new Set(arr.filter((ndx) => permitGroupsMap[ndx]))];
    const links = unique.map((ndx) => {
      const isGroup = ndx >= 100000;
      const path = isGroup
        ? `/data-management/permit-records/permit-groups-management/${
            ndx - 100000
          }`
        : `/data-management/permit-records/permits-management/${ndx}`;
      return buildLink(ndx, permitGroupsMap[ndx], path);
    });
    return renderTruncatedLinks(links);
  };

  const renderCell = (row, colId) => {
    switch (colId) {
      case "c_year":
        return row.c_year;
      case "c_month":
        return MONTH_NAMES[row.c_month] || row.c_month;
      case "meter_id":
        return row.meter_ndx ? (
          <StyledLink
            href={`/data-management/meter-readings/add-manage-meters/${row.meter_ndx}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopProp}
          >
            {row.meter_id}
          </StyledLink>
        ) : (
          row.meter_id
        );
      case "assoc_permit_groups":
        return renderPermitGroups(row.assoc_permit_groups);
      case "assoc_wells":
        return renderLinkedArray(
          row.assoc_wells,
          wellsMap,
          (ndx) => `/data-management/wells/wells-management/${ndx}`
        );
      case "assoc_permits":
        return renderLinkedArray(
          row.assoc_permits,
          permitsMap,
          (ndx) => `/data-management/permit-records/permits-management/${ndx}`
        );
      case "prev_meter_reading":
        return formatter(row.prev_meter_reading, 0) ?? "";
      case "meter_reading":
        return formatter(row.meter_reading, 0) ?? "";
      case "adjustment":
        return formatter(row.adjustment, 0) ?? "";
      case "mr_adjustment_reason_ndx":
        return adjustmentReasonMap[row.mr_adjustment_reason_ndx] || "";
      case "mr_report_status_ndx":
        return reportStatusMap[row.mr_report_status_ndx] || "";
      case "calculated_gallons":
        return formatter(row.calculated_gallons, 0) ?? "";
      case "notes":
        return row.notes || "";
      default:
        return row[colId];
    }
  };

  const renderSummaryCell = (row, colId) => {
    switch (colId) {
      case "fiscal_year":
        return `${row.fiscal_year - 1}–${row.fiscal_year}`;
      case "grouping_name": {
        const ndx = row.grouping_ndx;
        const isGroup = ndx >= 100000;
        const path = isGroup
          ? `/data-management/permit-records/permit-groups-management/${
              ndx - 100000
            }`
          : `/data-management/permit-records/permits-management/${ndx}`;
        return (
          <StyledLink href={path} target="_blank" rel="noopener noreferrer">
            {row.grouping_name}
          </StyledLink>
        );
      }
      case "permitted_gallons":
        return row.permitted_gallons != null
          ? formatter(row.permitted_gallons, 0)
          : "N/A";
      case "metered_gallons":
        return formatter(row.metered_gallons, 0) ?? "0";
      case "remaining_gallons":
        return row.remaining_gallons != null
          ? formatter(row.remaining_gallons, 0)
          : "N/A";
      case "pct_pumped":
        return row.pct_pumped != null
          ? `${(row.pct_pumped * 100).toFixed(1)}%`
          : "N/A";
      case "is_overpumped":
        return row.is_overpumped === true
          ? "Yes"
          : row.is_overpumped === false
          ? "No"
          : "N/A";
      case "is_over80pct":
        return row.is_over80pct === true
          ? "Yes"
          : row.is_over80pct === false
          ? "No"
          : "N/A";
      case "last_report":
        return row.last_report || "";
      default:
        return row[colId];
    }
  };

  const getSummaryCellStyle = (row, colId) => {
    if (colId === "is_overpumped" && row.is_overpumped === true) {
      return {
        backgroundColor: "orange",
        color: "white",
        fontWeight: 600,
      };
    }
    if (colId === "is_over80pct" && row.is_over80pct === true) {
      return {
        backgroundColor: "yellow",
        fontWeight: 600,
      };
    }
    return undefined;
  };

  if (error) return "An error has occurred: " + error.message;

  return (
    <>
      <SpinKeyframes />
      <Helmet title="Historical Meter Readings" />
      {!compactMode && (
        <>
          <Typography variant="h3" gutterBottom display="inline">
            Historical Meter Readings
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} exact to="/dashboard">
              Dashboard
            </Link>
            <Typography>Historical Meter Readings</Typography>
          </Breadcrumbs>

          <Divider my={6} />
        </>
      )}

      {!data || isLoading ? (
        <Loader />
      ) : (
        <>
          {/* ── Instructions ── */}
          <Alert severity="info" style={{ marginBottom: 12 }}>
            This data is refreshed nightly. Click a row to view the pumping
            summary for its permit owner/group across all fiscal years. Use{" "}
            <strong>Refresh Data</strong> if you need the latest readings. Note:
            pumping summaries are only available from approximately 2015 onward
            due to limited permitted volume records for earlier years.
          </Alert>

          {/* ── Summary Table (shown when a row is selected) ── */}
          {selectedSummaryRows.length > 0 && (
            <TableContainer
              component={Paper}
              style={{
                marginBottom: 16,
                border: "2px solid #ccc",
                borderRadius: 4,
                maxHeight: 300,
                overflowY: "auto",
              }}
            >
              <Box px={2} pt={1.5} pb={0.5}>
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                  Pumping Summary
                </Typography>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ display: "block", marginBottom: 4 }}
                >
                  {selectedSummaryRows.length} record
                  {selectedSummaryRows.length !== 1 ? "s" : ""} for selected
                  meter reading&apos;s permit group
                  {(selectedRow?.assoc_permit_groups?.length || 0) > 1
                    ? "s"
                    : ""}
                </Typography>
              </Box>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {SUMMARY_COLUMNS.map((col) => (
                      <SummaryHeaderCell key={col.id} align={col.align}>
                        {col.label}
                      </SummaryHeaderCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedSummaryRows.map((row) => {
                    const isHighlighted =
                      selectedRow?.fiscal_year === row.fiscal_year;
                    return (
                      <TableRow
                        key={`${row.grouping_ndx}-${row.fiscal_year}`}
                        style={
                          isHighlighted
                            ? { backgroundColor: "#fffde7" }
                            : undefined
                        }
                      >
                        {SUMMARY_COLUMNS.map((col) => {
                          const cellStyle = getSummaryCellStyle(row, col.id);
                          const highlightStyle = isHighlighted
                            ? {
                                fontWeight: 600,
                                backgroundColor: "#fffde7",
                              }
                            : undefined;
                          return (
                            <SummaryCell
                              key={col.id}
                              align={col.align}
                              style={{
                                ...highlightStyle,
                                ...cellStyle,
                              }}
                            >
                              {renderSummaryCell(row, col.id)}
                            </SummaryCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* ── Detail Table ── */}
          <Paper>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={2}
              pt={1.5}
            >
              <Typography variant="subtitle2" color="textSecondary">
                Showing {filteredAndSortedRows.length.toLocaleString()} of{" "}
                {(data?.length || 0).toLocaleString()} records
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <ButtonGroup size="small" variant="outlined">
                  <ToggleButton
                    className={!compactMode ? "active" : ""}
                    onClick={() => setCompactMode(false)}
                  >
                    Full
                  </ToggleButton>
                  <ToggleButton
                    className={compactMode ? "active" : ""}
                    onClick={() => setCompactMode(true)}
                  >
                    Compact
                  </ToggleButton>
                </ButtonGroup>
                <Tooltip title="Export filtered rows to CSV">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleExportCSV}
                    startIcon={<GetAppIcon style={{ fontSize: 16 }} />}
                    style={{ fontSize: "0.7rem", padding: "2px 10px" }}
                  >
                    CSV
                  </Button>
                </Tooltip>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setConfirmOpen(true)}
                  disabled={refreshing}
                  startIcon={
                    <RefreshIcon
                      style={{
                        fontSize: 16,
                        animation: refreshing
                          ? "spin 1s linear infinite"
                          : "none",
                      }}
                    />
                  }
                  style={{ fontSize: "0.7rem", padding: "2px 10px" }}
                >
                  {refreshing ? "Refreshing..." : "Refresh"}
                </Button>
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="Search all columns..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ fontSize: 18 }} color="action" />
                      </InputAdornment>
                    ),
                    style: { fontSize: "0.8rem" },
                  }}
                  style={{ width: 260 }}
                />
              </Box>
            </Box>
            <StyledTableContainer>
              <Table size="small" stickyHeader>
                <MeterReadingsHistoryHeader
                  columns={visibleColumns}
                  orderBy={orderBy}
                  order={order}
                  onSort={handleSort}
                  columnFilters={columnFilters}
                  filterOptions={filterOptions}
                  onFilterChange={handleFilterChange}
                />
                <TableBody>
                  {paginatedRows.map((row) => {
                    const hasGroups =
                      row.assoc_permit_groups &&
                      row.assoc_permit_groups.length > 0;
                    const isSelected =
                      hasGroups && selectedRowNdx === row.meter_reading_ndx;
                    const RowComponent = isSelected
                      ? SelectedDataRow
                      : hasGroups
                      ? DataRow
                      : PlainRow;
                    return (
                      <RowComponent
                        key={row.meter_reading_ndx}
                        onClick={
                          hasGroups ? () => handleRowClick(row) : undefined
                        }
                      >
                        {visibleColumns.map((col) => (
                          <Cell
                            key={col.id}
                            align={col.align}
                            style={
                              col.maxWidth
                                ? {
                                    maxWidth: col.maxWidth,
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }
                                : undefined
                            }
                          >
                            {renderCell(row, col.id)}
                          </Cell>
                        ))}
                      </RowComponent>
                    );
                  })}
                  {paginatedRows.length === 0 && (
                    <TableRow>
                      <Cell colSpan={visibleColumns.length} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No records match the current filters.
                        </Typography>
                      </Cell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
            <TablePagination
              component="div"
              count={filteredAndSortedRows.length}
              page={page}
              onChangePage={handleChangePage}
              rowsPerPage={perPage}
              onChangeRowsPerPage={handleChangePerPage}
              rowsPerPageOptions={[30, 60, 100, 200]}
            />
          </Paper>
        </>
      )}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          style: {
            border: "1px solid #ccc",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            minWidth: 200,
            maxWidth: 320,
            maxHeight: 300,
            overflowY: "auto",
          },
        }}
      >
        <Box px={2} py={1.5}>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ fontWeight: 600, marginBottom: 6, display: "block" }}
          >
            {popoverLinks.length} records
          </Typography>
          {popoverLinks.map((link) => (
            <Box
              key={link.key}
              py={0.5}
              style={{
                fontSize: "0.8rem",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              {link}
            </Box>
          ))}
        </Box>
      </Popover>

      {/* ── Refresh confirmation dialog ── */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Refresh Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will refresh the historical meter readings and pumping summary
            data from the database. This may take a moment. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="default">
            Cancel
          </Button>
          <Button
            onClick={handleRefresh}
            color="primary"
            variant="contained"
            disabled={refreshing}
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar feedback ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MeterReadingsHistory;
